import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/core/store";
import {
  addPosition,
  removeOrderItem,
  updateQuantity as updateItemQuantity,
  updateOrderComment,
  clearOrder,
  markPendingChanges,
} from "@/core/orders/reducer";
import {
  selectCartItems,
  selectCartTotal,
  selectCartItemsCount,
  selectCartComment,
  selectCartStats,
  selectOrdersLoading,
  selectOrdersError,
} from "@/core/orders/selectors";
import type {
  UseCartReturn,
  UseCartActionsReturn,
  CartOperationResult,
} from "@/core/orders/types";
import type { SelectedOption } from "@/core/options";
import type { MenuProduct } from "@/types";

/**
 * Мутаторы корзины — без единой подписки на стор.
 *
 * Зачем отдельно от `useCart`. Подписка живёт в компоненте, а не в хуке:
 * любой `useSelector` внутри вызванного хука перерисовывает вызвавший
 * компонент, даже если из всего возвращённого тот берёт один коллбэк.
 * Карточка списка зовёт корзину ровно ради `addItem` — а платила бы за
 * `isSyncing`, который на каждом цикле синхронизации честно меняется
 * false → true → false. Список карточек не виртуализован: на телефоне в
 * iframe это сотни перерисовок за цикл, которые никому ничего не показывают.
 *
 * Отсюда правило: тут не должно появиться ни одного `useAppSelector`. Нужно
 * прочитать корзину в коллбэке — читай через `useCart` (там состояние уже
 * подписано) или подпишись на точечный селектор в самом компоненте.
 *
 * Стережёт правило замер `__tests__/unit/orders/cart-card-rerender`, но
 * стережёт ЧАСТИЧНО: он ловит подписку только на те поля, которые двигают
 * `syncOrderStart` и `syncOrderError` (`isSyncing`, `lastSyncError`,
 * `pendingChanges`). Подписка на состав корзины или на цену пройдёт мимо
 * него — там перерисовка законна и замер её не отличит.
 *
 * Собственных вызовов у хука пока нет: обе быстрые карточки списка (`addItem`
 * ради «+») по-прежнему зовут `useCart()`, потому что замороженный
 * `__tests__/unit/options/quick-add-gate.test.tsx` мокает модуль фабрикой
 * `{ useCart }` — импорт `useCartActions` в карточке дал бы там `undefined`.
 * Как только тот тест можно будет тронуть, карточки переводятся сюда и
 * перестают платить за семь подписок `useCart` ради одного коллбэка.
 */
export const useCartActions = (): UseCartActionsReturn => {
  const dispatch = useAppDispatch();

  /**
   * Add item to cart with optional options
   * Supports quantity specification and option selection
   */
  const addItem = useCallback(
    (
      product: MenuProduct,
      quantity: number = 1,
      options?: SelectedOption[],
      itemComment?: string
    ): CartOperationResult => {
      try {
        // Validate inputs
        if (!product || !product.id) {
          return {
            success: false,
            error: "Invalid product data",
          };
        }

        if (quantity <= 0) {
          return {
            success: false,
            error: "Quantity must be greater than 0",
          };
        }

        // Синхронизацию заводит флаг pendingChanges, который ставит сам
        // редьюсер: её ведёт useCartSync, а не сага.
        dispatch(
          addPosition({
            product: product,
            quantity,
            options: options || [],
            comment: itemComment || "",
          })
        );

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to add item to cart";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch]
  );

  /**
   * Убрать позицию из корзины по её ключу (`itemKey`, не productId: двух
   * позиций одного товара с разными наборами опций productId не различает —
   * см. докблок мутаторов в reducer.ts).
   */
  const removeItem = useCallback(
    (itemKey: string): CartOperationResult => {
      try {
        if (!itemKey) {
          return {
            success: false,
            error: "Item key is required",
          };
        }

        dispatch(removeOrderItem({ itemKey }));

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove item from cart";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch]
  );

  /**
   * Поменять количество позиции, адресованной ключом `itemKey`.
   * Ноль и меньше — снять позицию.
   */
  const updateQuantity = useCallback(
    (itemKey: string, quantity: number): CartOperationResult => {
      try {
        if (!itemKey) {
          return {
            success: false,
            error: "Item key is required",
          };
        }

        if (quantity <= 0) {
          // Remove item if quantity is zero or less
          return removeItem(itemKey);
        }

        dispatch(updateItemQuantity({ itemKey, quantity }));

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update item quantity";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch, removeItem]
  );

  /**
   * Update cart comment
   * Allows order-level comments for special instructions
   */
  const updateComment = useCallback(
    (newComment: string): void => {
      dispatch(updateOrderComment({ comment: newComment }));
    },
    [dispatch]
  );

  /**
   * Clear entire cart
   * Resets cart to initial empty state
   * Triggers sync to update order on server with empty items array
   */
  const clearCart = useCallback((): void => {
    dispatch(clearOrder());
  }, [dispatch]);

  /**
   * Повтор синхронизации после ошибки.
   *
   * Взводит pendingChanges заново: триггером стал сам флаг, а прежний
   * syncOrderStart его СНИМАЕТ (reducer.ts) — повтор через него отменял бы
   * синхронизацию вместо запуска.
   */
  const retrySync = useCallback((): void => {
    dispatch(markPendingChanges());
  }, [dispatch]);

  // Мемо, а не литерал: у публичного хука сам объект — тоже часть контракта.
  // Коллбэки стабильны поодиночке, но новый контейнер на каждый рендер
  // перезапускал бы `useEffect(fn, [actions])` и пробивал бы React.memo у
  // потребителя, которому его передали пропсом.
  return useMemo(
    () => ({
      addItem,
      removeItem,
      updateQuantity,
      updateComment,
      clearCart,
      retrySync,
    }),
    [addItem, removeItem, updateQuantity, updateComment, clearCart, retrySync]
  );
};

/**
 * Custom hook for cart operations with automatic sum calculation
 * Provides clean interface for cart state management and operations
 *
 * Features:
 * - Real-time cart total calculation via Redux selectors
 * - Optimistic updates for immediate UI feedback
 * - Type-safe operations with proper error handling
 * - Automatic cart validation and business rules
 *
 * Подписки здесь держатся ТОЧЕЧНЫМИ — на производные, а не на слайс
 * `orders.currentOrder` целиком. Immer на мутации любого поля слайса
 * (`isSyncing`, `lastSyncError`, `pendingChanges`) рождает новую ссылку на
 * весь `currentOrder`, но вложенные `items` при этом не трогает; селекторы
 * ниже отдают в таких случаях прежние значения, и цикл синхронизации не
 * перерисовывает потребителей корзины. Нужен только мутатор, без состояния —
 * бери `useCartActions`.
 */
export const useCart = (): UseCartReturn => {
  const actions = useCartActions();

  // Redux state selectors
  const cartItems = useAppSelector(selectCartItems);
  const totalSum = useAppSelector(selectCartTotal);
  const itemCount = useAppSelector(selectCartItemsCount);
  const cartStats = useAppSelector(selectCartStats);

  // Extract individual state pieces
  const comment = useAppSelector(selectCartComment);
  const loading = useAppSelector(selectOrdersLoading);
  const error = useAppSelector(selectOrdersError);

  /**
   * Get item by ID helper
   * Useful for component-level item operations
   */
  const getItemById = useCallback(
    (itemId: string) => {
      return cartItems.find(
        (item) => item.productSnapshot.productId === itemId || item.productId === itemId
      );
    },
    [cartItems]
  );

  /**
   * Check if cart is empty
   */
  const isEmpty = cartStats.isEmpty;

  /**
   * Get cart validation status
   * Can be extended with business rules
   */
  const isValid = cartItems.length > 0;

  return {
    // State
    cartItems,
    totalSum,
    itemCount,
    comment,
    loading,
    error,

    // Computed state
    isEmpty,
    isValid,
    cartStats,

    // Operations
    ...actions,

    // Helpers
    getItemById,
  };
};

export default useCart;
